import { FC, Ref, useMemo, useState } from "react";
import { RegisterOptions } from "react-hook-form";

import { Edit, Eye, Lock, SquarePlus } from "shared/assets/icons";
import { Tooltip } from "shared/components";
import { CheckBoxWrapper } from "shared/components/Form/Fields/Option/styles";
import { Icon, MutedText, StatusText, TinyText } from "shared/styles";
import { IStatus, PermissionType } from "shared/types";

import { sanitizeId } from "../helpers";

import {
  StyledPermissionField,
  StyledPermissionFieldProps,
  StyledPermissionIcon,
  StyledPermissionLabel,
  StyledPermissionLockIcon,
  StyledPermissionText,
} from "./styles";

export interface PermissionFieldBaseProps extends StyledPermissionFieldProps {
  name: string;
  label: string;
  description?: string;
  register: (rules?: RegisterOptions) => Ref<any>;
  permissionType: PermissionType;
  value: boolean;
  defaultValue?: boolean;
  disabled?: boolean;
  locked?: boolean;
}

interface PermissionFieldProps extends PermissionFieldBaseProps {
  handleChange?: (checked: boolean) => void;
  hoverText?: string;
}

export const PermissionField: FC<PermissionFieldProps> = ({
  name,
  label,
  description,
  register,
  permissionType,
  value,
  defaultValue,
  disabled,
  locked,
  handleChange,
  hoverText,
  ...rest
}) => {
  const renderIcon = () => {
    switch (permissionType) {
      case PermissionType.create:
        return <Icon as={SquarePlus} />;
      case PermissionType.edit:
        return <Icon as={Edit} />;
      default:
        return <Icon as={Eye} />;
    }
  };

  const renderSubIcon = () => (
    <StyledPermissionLockIcon>
      <Icon as={Lock} size="s" />
    </StyledPermissionLockIcon>
  );

  const isDisabled = useMemo(() => locked || disabled, [locked, disabled]);
  const [referenceElement, setReferenceElement] = useState<Element | null>();

  return (
    <StyledPermissionField
      {...rest}
      ref={setReferenceElement}
      data-testid={sanitizeId(name)}
    >
      <StyledPermissionLabel htmlFor={sanitizeId(name)} isDisabled={isDisabled}>
        <StyledPermissionIcon checked={value}>
          {renderIcon()}
          {locked && renderSubIcon()}
        </StyledPermissionIcon>
        <StyledPermissionText>
          <TinyText>
            {label}{" "}
            {locked && (
              <StatusText status={IStatus.warning}>(always on)</StatusText>
            )}
          </TinyText>
          {description && <MutedText>{description}</MutedText>}
        </StyledPermissionText>
        <CheckBoxWrapper>
          <input
            id={sanitizeId(name)}
            name={name}
            type="checkbox"
            ref={register()}
            defaultChecked={defaultValue}
            disabled={isDisabled}
            onChange={(event) =>
              handleChange && handleChange(event.target.checked)
            }
          />
        </CheckBoxWrapper>
      </StyledPermissionLabel>
      {isDisabled && hoverText && (
        <Tooltip
          content={<span>{hoverText}</span>}
          referenceElement={referenceElement}
          placement="top-end"
          maxWidth="none"
        />
      )}
    </StyledPermissionField>
  );
};
